export type SparqlResults<Keys extends string, OptionalKeys extends string = never> = {
  head: {
    vars: (Keys & OptionalKeys)[]
  },
  results: {
    bindings: Array<
      { [key in Keys]: RDFData } &
      { [key in OptionalKeys]?: RDFData }
    >
  }
}

export type OpaqueSparqlResults = SparqlResults<string, string>
type XmlSchemaType = typeof XML_SCHEMA[keyof typeof XML_SCHEMA]

export const XML_SCHEMA = {
  STRING:             'http://www.w3.org/2001/XMLSchema#string',
  NORMALIZED_STRING:  'http://www.w3.org/2001/XMLSchema#normalizedString',
  TOKEN:              'http://www.w3.org/2001/XMLSchema#token',
  ANY_URI:            'http://www.w3.org/2001/XMLSchema#anyURI',

  BOOLEAN:            'http://www.w3.org/2001/XMLSchema#boolean',
  DECIMAL:            'http://www.w3.org/2001/XMLSchema#decimal',
  INTEGER:            'http://www.w3.org/2001/XMLSchema#integer',
  FLOAT:              'http://www.w3.org/2001/XMLSchema#float',
  DOUBLE:             'http://www.w3.org/2001/XMLSchema#double',

  LONG:               'http://www.w3.org/2001/XMLSchema#long',
  INT:                'http://www.w3.org/2001/XMLSchema#int',
  SHORT:              'http://www.w3.org/2001/XMLSchema#short',
  BYTE:               'http://www.w3.org/2001/XMLSchema#byte',

  UNSIGNED_LONG:      'http://www.w3.org/2001/XMLSchema#unsignedLong',
  UNSIGNED_INT:       'http://www.w3.org/2001/XMLSchema#unsignedInt',
  UNSIGNED_SHORT:     'http://www.w3.org/2001/XMLSchema#unsignedShort',
  UNSIGNED_BYTE:      'http://www.w3.org/2001/XMLSchema#unsignedByte',

  NON_NEGATIVE_INTEGER: 'http://www.w3.org/2001/XMLSchema#nonNegativeInteger',
  NON_POSITIVE_INTEGER: 'http://www.w3.org/2001/XMLSchema#nonPositiveInteger',
  POSITIVE_INTEGER:     'http://www.w3.org/2001/XMLSchema#positiveInteger',
  NEGATIVE_INTEGER:     'http://www.w3.org/2001/XMLSchema#negativeInteger',

  DATETIME:           'http://www.w3.org/2001/XMLSchema#dateTime',
  DATETIME_STAMP:     'http://www.w3.org/2001/XMLSchema#dateTimeStamp',
  DATE:               'http://www.w3.org/2001/XMLSchema#date',
  TIME:               'http://www.w3.org/2001/XMLSchema#time',
  DURATION:           'http://www.w3.org/2001/XMLSchema#duration',
  DAY_TIME_DURATION:  'http://www.w3.org/2001/XMLSchema#dayTimeDuration',
  YEAR_MONTH_DURATION:'http://www.w3.org/2001/XMLSchema#yearMonthDuration',
  G_YEAR:             'http://www.w3.org/2001/XMLSchema#gYear',
  G_YEAR_MONTH:       'http://www.w3.org/2001/XMLSchema#gYearMonth',
  G_MONTH:            'http://www.w3.org/2001/XMLSchema#gMonth',
  G_MONTH_DAY:        'http://www.w3.org/2001/XMLSchema#gMonthDay',
  G_DAY:              'http://www.w3.org/2001/XMLSchema#gDay',

  BASE64_BINARY:      'http://www.w3.org/2001/XMLSchema#base64Binary',
  HEX_BINARY:         'http://www.w3.org/2001/XMLSchema#hexBinary',
} as const

type Uri = { type: 'uri', value: string }
type LiteralTyped = { type: 'literal', value: string, datatype: XmlSchemaType }
type LiteralLang  = { type: 'literal', value: string, "xml:lang": string }
type LiteralPlain = { type: 'literal', value: string }

export type RDFData = Uri | LiteralTyped | LiteralLang | LiteralPlain

export function isSameData(a: RDFData, b: RDFData): boolean {
  if (a.value !== b.value) return false
  if (a.type !== b.type) return false
  if (a.type === 'uri') return true // see Uri
  if (!('datatype' in a) && !('datatype' in b)) return true // see LiteralString
  if (!('datatype' in a) || !('datatype' in b)) return false
  return a.datatype === b.datatype
}

export function getValueAsUri(data: RDFData): string {
  if (data.type !== "uri")
    throw new Error(`Expected type uri, got ${data.type} for ${data.value}`)

  return data.value
}

export function getValueAsLiteralString(data: RDFData): string {
  if (data.type !== "literal")
    throw new Error(`Expected type literal, got ${data.type} for ${data.value}`)

  return data.value
}

export function getValueAsLiteralOrUri(data: RDFData): string {
  if (!isTypeAnyOf(data.type, ["literal", "uri"]))
    throw new Error(`Expected type literal or uri, got ${data.type} for ${data.value}`)

  return data.value
}

export function getValueAsLiteralBoolean(data: RDFData): boolean {
  if (data.type !== "literal")
    throw new Error(`Expected type literal, got ${data.type} for ${data.value}`)

  if (!("datatype" in data))
    throw Error(`No datatype for ${data.value}, was expecting boolean`)

  if (data.datatype !== XML_SCHEMA.BOOLEAN)
    throw new Error(`Wrong datatype for ${data.value}, was expecting boolean`)

  return data.value === "true"
}

export function getValueAsLiteralNumber(data: RDFData): number {
  if (data.type !== "literal")
    throw new Error(`Expected type literal, got ${data.type} for ${data.value}`)

  if (!("datatype" in data))
    throw Error(`No datatype for ${data.value}, was expecting float or integer`)

  if (!isTypeAnyOf(data.datatype, [XML_SCHEMA.FLOAT, XML_SCHEMA.DOUBLE, XML_SCHEMA.INTEGER]))
    throw new Error(`Expected double, float or integer datatype, got ${data.datatype} for ${data.value}`)

  return parseFloat(data.value)
}

/**
 * Unlike {@see getValueAsLiteralNumber}, this also allows strings containing numbers
 */
export function getValueAsLiteralNumberFuzzy(data: RDFData): number {
  if (data.type !== "literal")
    throw new Error(`Expected type literal, got ${data.type} for ${data.value}`)

  return parseFloat(data.value)
}

function isTypeAnyOf<T, U extends T>(type: T, types: U[]): type is U {
  return types.includes(type as U);
}
